import * as React from 'react';

import {
    GroupedComboBoxOnChangeProps,
    IGroupedComboBox,
    IGroupedComboBoxGroup,
    IGroupedComboBoxItem,
    isKeyboardEvent,
    ItemOnClickCallback,
    ItemSecondaryActionCallback,
    RefreshSubmenuCallback,
} from 'grouped-combobox-core';
import { GlobalContext } from '../common/globalContext';
import { IFont, IFontPickerPropsBase } from './FontPicker.types.shared';
import { GroupedComboBoxRenderers } from './createGroups/createGroups.types';
import { fontInfoItemKey } from '../common/constants';
import { DefaultStringResources } from '../common/defaultStringResources';
import { createGroups } from './createGroups/createGroups';
import { useCreateGroupsOnDemand } from './createGroups/useCreateGroupsOnDemand';
import { getFontKey, isFontFamily, isMruEntry } from '../common/fontUtils';
import { addFontToPinnedGroup, removeFontFromPinnedGroup } from './fontFamilyPinning';
import { addVariantToPinnedSection, removeVariantFromPinnedSection } from './fontVariantPinning';

import { getFontAndPinnedState } from '../common/pinning';
import { assertFontFamily } from '../common/assertFonts';
import { assert } from '../common/assert';
import { findFontOrVariant } from '../common/findFontOrVariant';
import { createOnAnnounceSearchResult } from '../common/announceText';
import { isPinningIcon, canShowIcon, fontToIcon } from '../common';

export type FontPickerRenderProps = [
    comboBoxRef: React.RefObject<IGroupedComboBox>,
    groups: IGroupedComboBoxGroup[],
    appContext: GlobalContext,
    onMenuOpened: () => void,
    onMenuDismissed: () => void,
    ontItemClick: ItemOnClickCallback,
    onItemSecondaryAction: ItemSecondaryActionCallback,
    onItemPreview: (
        item: IGroupedComboBoxItem,
        ev?: React.FocusEvent<unknown>
    ) => string | undefined,
    onFontChange: (props: GroupedComboBoxOnChangeProps, ev: React.KeyboardEvent<unknown>) => void,
    onAnnounceSearchResult: (results: IGroupedComboBoxGroup[], filterText: string) => string
];

export function useFontPickerCore(
    props: IFontPickerPropsBase,
    renderers: GroupedComboBoxRenderers,
    setShowingItemPreview: (newState: boolean) => void
): FontPickerRenderProps {
    const {
        isSubscriber = false,
        onFontPinned,
        onFontUnpinned,
        onFontPreview,
        componentRef,
        onFontSelected,
        onFontInfoRequested,
        onMenuOpened,
        onMenuDismissed,
        stringResources = DefaultStringResources,
        iconResources,
        onFontChange,
        mruList,
        fonts,
        menuInitiallyOpened,
        allowFontInfoPane = true,
        allowPinning = true,
    } = props;

    const comboBox = React.useRef<IGroupedComboBox>(null);

    const [groups, setGroups] = React.useState<IGroupedComboBoxGroup[]>(
        menuInitiallyOpened ? createGroups(props, renderers) : []
    );

    const menuOpenedCallback = useCreateGroupsOnDemand(
        props,
        setGroups,
        comboBox,
        renderers,
        onMenuOpened
    );

    const menuDismissedCallback = React.useCallback(() => {
        setShowingItemPreview(false);
        onMenuDismissed?.();
    }, [onMenuDismissed, setShowingItemPreview]);

    const onFontChangeCallback = React.useCallback(
        (props: GroupedComboBoxOnChangeProps, ev: React.KeyboardEvent<unknown>): void => {
            const { text: fontName } = props;

            // Try searching for a font family or font variant which matches the typed text
            const matchedFont = findFontOrVariant(fontName, fonts, mruList ?? []);

            if (matchedFont) {
                if (
                    !isSubscriber &&
                    isFontFamily(matchedFont) &&
                    matchedFont.fontType === 'premium'
                ) {
                    onFontInfoRequested?.(matchedFont, 'premium', ev);
                    return;
                }

                // This will make GroupedComboBox display the actual item in the textbox.
                props.selectedItem = {
                    // Use the name from 'matchedFont', and not 'fontName', so that we show the correct casing.
                    // For example, if user types 'arial', we will update it to become 'Arial'
                    name: matchedFont.name,
                    key: getFontKey(matchedFont),
                };

                if (onFontSelected) {
                    ev.persist();

                    window.requestAnimationFrame(() => {
                        // If we find a matching font, we treat it as if user has selected it from the drop down.
                        // Thus we raise the onFontSelected event, rather than onFontChange.
                        onFontSelected(matchedFont, ev);
                    });
                }
            } else {
                if (onFontChange) {
                    // React reuses event arg objects among the events. As a result, if we try to access 'ev'
                    // from inside the handler of requestAnimationframe, we may actually access the wrong data,
                    // unless we call ev.persist() (as we do on line 119 above).
                    // But a better option is to save the keyCode and access it from inside the lambda.
                    const keyCode = ev.key;
                    window.requestAnimationFrame(() => {
                        onFontChange(fontName, keyCode);
                    });
                }
            }
        },
        [fonts, mruList, onFontChange, onFontSelected, onFontInfoRequested, isSubscriber]
    );

    const onAnnounceSearchResult = React.useMemo(
        () => createOnAnnounceSearchResult(stringResources),
        [stringResources]
    );

    const onFontIconButtonClick = React.useCallback(
        (
            font: IFont,
            isPinned: boolean,
            ev?: React.KeyboardEvent<unknown> | React.MouseEvent<unknown>,
            refreshContainingMenu?: RefreshSubmenuCallback
        ): boolean => {
            if (isFontFamily(font)) {
                const { fontKey, fontType } = font;
                if (fontType === 'premium' && !isSubscriber) {
                    onFontInfoRequested?.(font, 'premium', ev);
                    comboBox.current?.dismissMenu(ev);
                    return false;
                }

                if (isPinned) {
                    setGroups((currentGroups) => removeFontFromPinnedGroup(currentGroups, fontKey));
                    comboBox.current?.announceText(
                        stringResources.unPinnedFontAnnouncement.replace('{0}', font.name)
                    );
                    onFontUnpinned && onFontUnpinned(font, ev);
                } else {
                    setGroups((currentGroups) => addFontToPinnedGroup(currentGroups, fontKey));
                    comboBox.current?.announceText(
                        stringResources.pinnedFontAnnouncement.replace('{0}', font.name)
                    );
                    onFontPinned && onFontPinned(font, ev);
                }
            } else if (isMruEntry(font)) {
                const { isThemeFont, variantType } = font;

                if (isThemeFont) {
                    onFontInfoRequested?.(font, 'theme', ev);
                    comboBox.current?.dismissMenu(ev);
                    return false;
                } else if (
                    variantType === 'missing' ||
                    variantType === 'embedded' ||
                    variantType === 'compatible'
                ) {
                    onFontInfoRequested?.(font, variantType, ev);
                    comboBox.current?.dismissMenu(ev);
                    return false;
                }
            } /* font variant */ else {
                // We call setGroups() here just so that we can access 'currentGroups' value. We will NOT update the state here.
                setGroups((currentGroups) => {
                    // font.parentKey must be defined.
                    assert(font.parentKey);

                    const operation = isPinned
                        ? removeVariantFromPinnedSection
                        : addVariantToPinnedSection;

                    const newSections = operation(currentGroups, font.variantKey, font.parentKey);

                    if (newSections !== null) {
                        // If we're pinning or unpinning a font variant, refreshContainingMenu must be defined.
                        assert(refreshContainingMenu);
                        refreshContainingMenu(newSections);
                    }

                    // Always return the same value here to avoid a re-render.
                    return currentGroups;
                });

                if (isPinned) {
                    comboBox.current?.announceText(
                        stringResources.unPinnedFontAnnouncement.replace('{0}', font.name)
                    );
                    onFontUnpinned?.(font, ev);
                } else {
                    comboBox.current?.announceText(
                        stringResources.pinnedFontAnnouncement.replace('{0}', font.name)
                    );
                    onFontPinned?.(font, ev);
                }
            }

            return true;
        },
        [
            isSubscriber,
            onFontInfoRequested,
            onFontPinned,
            onFontUnpinned,
            stringResources.pinnedFontAnnouncement,
            stringResources.unPinnedFontAnnouncement,
        ]
    );

    const appContext = React.useMemo<GlobalContext>(
        () => ({
            isSubscriber: isSubscriber || false,
            fontIconButtonClicked: onFontIconButtonClick,
            stringResources,
            iconResources,
        }),
        [isSubscriber, onFontIconButtonClick, stringResources, iconResources]
    );

    React.useImperativeHandle(
        componentRef,
        () => ({
            dismissMenu: (): void => comboBox.current?.dismissMenu(),

            focus: (shouldOpenOnFocus?: boolean, useFocusAsync?: boolean): boolean =>
                comboBox.current?.focus(shouldOpenOnFocus, useFocusAsync) || false,

            get isDropdownOpen(): boolean {
                return comboBox.current?.isDropdownOpen ?? false;
            },
        }),
        []
    );

    const onItemClickCallback = React.useCallback(
        (
            clickedItem: IGroupedComboBoxItem,
            ev?: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>
        ) => {
            const { itemKey } = clickedItem;

            const [font] = getFontAndPinnedState(clickedItem);

            if (
                itemKey === fontInfoItemKey ||
                (!isSubscriber && isFontFamily(font) && font.fontType === 'premium')
            ) {
                const isTabKey = ev && isKeyboardEvent(ev) && ev.key == 'Tab';
                if (!isTabKey) {
                    assertFontFamily(font);
                    onFontInfoRequested?.(
                        font,
                        itemKey === fontInfoItemKey ? 'about' : 'premium',
                        ev
                    );
                }

                // if the "About this font" is clicked, we don't want
                // to update the textbox to show it.
                ev?.preventDefault();
            } else {
                if (onFontSelected) {
                    ev?.persist();
                    window.requestAnimationFrame(() => onFontSelected(font, ev));
                }
            }
        },
        [isSubscriber, onFontInfoRequested, onFontSelected]
    );

    const onItemSecondaryActionCallback = React.useCallback(
        (
            clickedItem: IGroupedComboBoxItem,
            refreshSubmenu: RefreshSubmenuCallback,
            ev?: React.KeyboardEvent<unknown>
        ) => {
            const { itemKey } = clickedItem;
            const [font, isPinned] = getFontAndPinnedState(clickedItem);

            if (itemKey === fontInfoItemKey || !canShowIcon(font, isSubscriber)) {
                onItemClickCallback(clickedItem, ev);
                return false; // Don't refresh icon
            }

            if (isPinningIcon(fontToIcon(clickedItem, isSubscriber, true, iconResources))) {
                // If the icon has to do with pinning and pinning is disabled, activate the font instead
                if (!allowPinning) {
                    onItemClickCallback(clickedItem, ev);
                    return false; // Don't refresh icon
                }
            } else if (!allowFontInfoPane) {
                // If it's not a pinning related icon but the font info pane is disabled, activate the font instead
                onItemClickCallback(clickedItem, ev);
                return false; // Don't refresh icon
            }

            return onFontIconButtonClick(font, isPinned, ev, refreshSubmenu);
        },
        [
            allowFontInfoPane,
            allowPinning,
            iconResources,
            isSubscriber,
            onFontIconButtonClick,
            onItemClickCallback,
        ]
    );

    const onItemPreviewCallback = React.useCallback(
        (item: IGroupedComboBoxItem, ev?: React.FocusEvent<unknown>) => {
            const { itemKey } = item;
            if (itemKey === fontInfoItemKey) {
                // if the "About this font" item gets focused, we don't want to set it as pending text
                ev?.preventDefault();
                return;
            }

            setShowingItemPreview(true);

            if (onFontPreview) {
                const [font] = getFontAndPinnedState(item);
                return onFontPreview(font, ev);
            }
        },
        [onFontPreview, setShowingItemPreview]
    );

    return [
        comboBox,
        groups,
        appContext,
        menuOpenedCallback,
        menuDismissedCallback,
        onItemClickCallback,
        onItemSecondaryActionCallback,
        onItemPreviewCallback,
        onFontChangeCallback,
        onAnnounceSearchResult,
    ];
}
